import React, { useState, useContext } from "react";
import StatusList from "./StatusList";
import CreateStatusForm from "./CreateStatusForm";
import EditStatusForm from "./EditStatusForm";
import { AuthContext } from "../../contexts/AuthContext";

const StatusPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [editingStatus, setEditingStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const handleStatusCreated = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1); // Trigger list refresh
  };

  const handleStatusUpdated = () => {
    setEditingStatus(null);
    setRefreshTrigger((prev) => prev + 1); // Trigger list refresh
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>Status Management</h2>
          <p className="text-muted">
            Manage system statuses and their types.
            {!isAdmin && " (View-only access)"}
          </p>
        </div>
      </div>

      {/* Global Data Notice */}
      <div
        className="alert alert-info d-flex align-items-center mb-4"
        role="alert"
      >
        <i className="bi bi-globe fs-4 me-3"></i>
        <div>
          <strong>Global Reference Data:</strong> Statuses are shared across all
          tenants. Only administrators can make modifications.
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("list");
              setEditingStatus(null);
            }}
          >
            <i className="bi bi-list-ul"></i> Status List
          </button>
        </li>
        {isAdmin && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "create" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("create");
                setEditingStatus(null);
              }}
            >
              <i className="bi bi-plus-circle"></i> Create Status
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "list" && (
          <div className="tab-pane active">
            {!editingStatus ? (
              <StatusList
                onEdit={(status) => {
                  if (isAdmin) {
                    setEditingStatus(status);
                  }
                }}
                refreshTrigger={refreshTrigger}
              />
            ) : (
              <EditStatusForm
                key={editingStatus.status_id}
                status={editingStatus}
                onCancel={() => setEditingStatus(null)}
                onUpdated={handleStatusUpdated}
              />
            )}
          </div>
        )}

        {activeTab === "create" && isAdmin && (
          <div className="tab-pane active">
            <CreateStatusForm onCreated={handleStatusCreated} />
          </div>
        )}
      </div>

      {/* Non-admin info message */}
      {!isAdmin && (
        <div className="alert alert-warning mt-4" role="alert">
          <i className="bi bi-info-circle"></i> You have view-only access to
          status management. Contact an administrator to make changes.
        </div>
      )}

      {/* HIPAA Compliance Footer */}
      <div className="mt-5 p-3 bg-light rounded">
        <h6 className="text-muted">
          <i className="bi bi-shield-check"></i> HIPAA Compliance & Security
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All status modifications are logged for audit purposes</li>
            <li>Statuses are global reference data (no PHI)</li>
            <li>Changes affect all tenants system-wide</li>
            <li>Only administrators can modify statuses</li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default StatusPage;
